import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { financialService } from "../services/financialService";
import { usePermissions } from "../hooks/usePermissions";

const money = (value: string | number) => `UGX ${Number(value).toLocaleString()}`;

/** A staff member's own payslips, each shown once that month's payroll is approved. */
export default function MyPayslipsCard() {
  const { hasCapability } = usePermissions();
  const isEmployee = hasCapability("access.core.any_employee");
  const payslips = useQuery({
    queryKey: ["my-payslips"],
    queryFn: financialService.getMyPayslips,
    enabled: isEmployee,
  });

  if (!isEmployee) return null;

  return (
    <section className="card">
      <div className="card-content space-y-3">
        <h2 className="flex items-center gap-2 font-semibold">
          <FileText className="h-5 w-5" />
          My payslips
        </h2>
        {payslips.isLoading && <p className="text-sm text-gray-500">Loading payslips…</p>}
        {payslips.isError && <p className="text-sm text-red-700">Your payslips could not be loaded.</p>}
        {payslips.data?.length === 0 && (
          <p className="text-sm text-gray-500">
            No payslips yet. Each month&apos;s payslip appears here once that payroll is approved.
          </p>
        )}
        {!!payslips.data?.length && (
          <ul className="divide-y text-sm">
            {payslips.data.map((payslip) => (
              <li key={payslip.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <div className="font-medium">{payslip.period_name}</div>
                  <div className="text-xs text-gray-500">
                    Gross {money(payslip.gross_salary)} · take-home {money(payslip.net_pay)}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-outline px-3 py-1 text-sm"
                  onClick={() => financialService.downloadPayslipPdf(payslip.id)}
                >
                  Download PDF
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
